import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Popup from 'reactjs-popup';

import { ALL_COLUMNS, DEFAULT_VISIBLE } from '../constants';

const META = Object.fromEntries(ALL_COLUMNS.map((c) => [c.key, c]));

// Popup renders content in a portal (outside .ab), so style the box inline —
// the inner .ab-setup__* classes are global and still apply.
const panelStyle = {
  width: '392px',
  padding: 0,
  border: '1px solid var(--sl-border-secondary)',
  borderRadius: '12px',
  background: 'var(--sl-bg-primary)',
  boxShadow: 'var(--sl-shadow-lg)',
  overflow: 'hidden',
};

const GripIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
    <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
    <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
  </svg>
);

const ArrowUp = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 19V6" /><path d="M6 12l6-6 6 6" /></svg>
);
const ArrowDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v13" /><path d="M6 12l6 6 6-6" /></svg>
);

const TableSetupPopover = ({ columns, setColumns, sort, dir, setSort }) => {
  const [dragKey, setDragKey] = useState(null);

  const visibleCount = columns.filter((c) => c.visible).length;

  const toggle = (key) => {
    if (META[key].locked) return;
    setColumns(columns.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)));
  };

  const reorder = (fromKey, toKey) => {
    if (fromKey === toKey) return;
    const next = [...columns];
    const from = next.findIndex((c) => c.key === fromKey);
    const to = next.findIndex((c) => c.key === toKey);
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setColumns(next);
  };

  const reset = () => {
    setColumns(
      [...DEFAULT_VISIBLE, ...ALL_COLUMNS.map((c) => c.key).filter((k) => !DEFAULT_VISIBLE.includes(k))]
        .map((key) => ({ key, visible: DEFAULT_VISIBLE.includes(key) })),
    );
    setSort('spotify', 'desc');
  };

  const sortLabel = META[ALL_COLUMNS.find((c) => c.sortKey === sort)?.key || 'spotify']?.label || 'Spotify';

  const trigger = (
    <button type="button" className="ab-setup__trigger">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <path d="M4 6h16" /><path d="M4 12h10" /><path d="M4 18h6" />
      </svg>
      <span>Table setup</span>
      <span className="ab-setup__count">{visibleCount} cols · 1 sort</span>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
    </button>
  );

  return (
    <div className="ab-setup">
      <Popup
        trigger={trigger}
        position="bottom right"
        on="click"
        arrow={false}
        offsetY={8}
        closeOnDocumentClick
        contentStyle={panelStyle}
      >
        <>
          <div className="ab-setup__panelhead">
            <span>Table setup</span>
            <button type="button" className="ab-clear" onClick={reset}>Reset</button>
          </div>

          <div className="ab-setup__sorted">
            <div className="ab-setup__eyebrow">Sorted by</div>
            <div className="ab-sortchip">
              <span>{sortLabel}</span>
              <span className="ab-sortchip__arrow">{dir === 'asc' ? '↑' : '↓'}</span>
            </div>
          </div>

          <div className="ab-setup__cols">
            <div className="ab-setup__eyebrow">Columns — drag to reorder</div>
            {columns.map(({ key, visible }) => {
              const col = META[key];
              const isSorted = col.sortKey && col.sortKey === sort;
              return (
                <div
                  key={key}
                  className={`ab-colrow ${dragKey === key ? 'ab-colrow--dragging' : ''}`}
                  draggable
                  onDragStart={() => setDragKey(key)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { reorder(dragKey, key); setDragKey(null); }}
                  onDragEnd={() => setDragKey(null)}
                >
                  <span className="ab-grip"><GripIcon /></span>
                  <button
                    type="button"
                    aria-label={`Toggle ${col.label} column`}
                    aria-pressed={visible}
                    className={`ab-check ${visible ? 'ab-check--on' : ''} ${col.locked ? 'ab-check--locked' : ''}`}
                    onClick={() => toggle(key)}
                  >
                    {visible && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" style={{ stroke: 'var(--sl-fg-white)' }}
                        strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12.5l5.5 5.5L20 6.5" /></svg>
                    )}
                  </button>
                  <div className="ab-colrow__label">
                    <span>{col.label}</span>
                    <span className="ab-colrow__meta">{col.locked ? 'Locked' : col.meta}</span>
                  </div>
                  {col.sortKey && (
                    <div className="ab-colrow__sort">
                      <button
                        type="button"
                        title="Sort ascending"
                        className={isSorted && dir === 'asc' ? 'ab-sortbtn--on' : ''}
                        onClick={() => setSort(col.sortKey, 'asc')}
                      ><ArrowUp /></button>
                      <button
                        type="button"
                        title="Sort descending"
                        className={isSorted && dir === 'desc' ? 'ab-sortbtn--on' : ''}
                        onClick={() => setSort(col.sortKey, 'desc')}
                      ><ArrowDown /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="ab-setup__foot">{columns.length - visibleCount} columns hidden</div>
        </>
      </Popup>
    </div>
  );
};
TableSetupPopover.propTypes = {
  columns: PropTypes.array.isRequired,
  setColumns: PropTypes.func.isRequired,
  sort: PropTypes.string.isRequired,
  dir: PropTypes.string.isRequired,
  setSort: PropTypes.func.isRequired,
};

export default TableSetupPopover;
